import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Vehicle } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { usePermissions } from '../../../hooks/usePermissions';
import VehicleSidebar from '../components/VehicleSidebar';
import VehicleAboutSection from '../components/VehicleAboutSection';
import VehicleTechnicalSettingsSection from '../components/VehicleTechnicalSettingsSection';
import VehicleHistorySection from '../components/VehicleHistorySection';
import VehicleTripInspectionHistorySection from '../components/VehicleTripInspectionHistorySection';
import VehiclePreventativeMaintenanceSection from '../components/VehiclePreventativeMaintenanceSection';
import VehicleDefectsSection from '../components/VehicleDefectsSection';
import VehicleComponentsSection from '../components/VehicleComponentsSection';
import VehicleBlueBookRatesSection from '../components/VehicleBlueBookRatesSection';
import VehicleAttachmentsSection from '../components/VehicleAttachmentsSection';
import VehicleRepairHistorySection from '../components/VehicleRepairHistorySection';
import { Alert, Button, Col, Row, Spinner } from '@/components/ui';

type SidebarItem = 'about' | 'trip-inspection-history' | 'preventative-maintenance' | 'vehicle-history' | 'vehicle-defects' | 'technical-settings' | 'vehicle-components' | 'blue-book-rates' | 'vehicle-attachments' | 'repair-history';

const SIDEBAR_ITEMS: SidebarItem[] = ['about', 'trip-inspection-history', 'preventative-maintenance', 'vehicle-history', 'vehicle-defects', 'technical-settings', 'vehicle-components', 'blue-book-rates', 'vehicle-attachments', 'repair-history'];

/** V3-025: the list's ⋮ links straight to a section with `?section=`; anything else opens About. */
const sectionFromParam = (value: string | null): SidebarItem =>
  value && (SIDEBAR_ITEMS as string[]).includes(value) ? (value as SidebarItem) : 'about';

const VehicleDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<SidebarItem>(() => sectionFromParam(searchParams.get('section')));
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const canEditVehicle = hasPermission('vehicle:edit');

  useEffect(() => {
    if (id) {
      fetchVehicle();
    }
  }, [id]);

  const fetchVehicle = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const vehicleData = await vehicleService.getVehicleById(parseInt(id));
      setVehicle(vehicleData);
      setError(null);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      if (error instanceof Error && error.message.includes('not found')) {
        setError(error.message);
      } else {
        setError('Failed to load vehicle. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVehicle = async (updatedVehicle: Vehicle) => {
    setVehicle(updatedVehicle);
    await fetchVehicle(); // Refresh to get latest data
  };

  if (loading) {
    return (
      <div className="tw-w-full tw-p-4">
        <div className="tw-text-center tw-py-5">
          <Spinner role="status">
            <span className="tw-sr-only">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="tw-w-full tw-p-4">
        <Alert variant="danger">
          {error || 'Vehicle not found'}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/vehicles')}>
          Back to Vehicles
        </Button>
      </div>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'about':
        return <VehicleAboutSection vehicle={vehicle} onUpdate={handleUpdateVehicle} canEdit={canEditVehicle} />;
      case 'technical-settings':
        return <VehicleTechnicalSettingsSection vehicle={vehicle} onUpdate={handleUpdateVehicle} canEdit={canEditVehicle} />;
      case 'vehicle-history':
        return <VehicleHistorySection vehicle={vehicle} />;
      case 'trip-inspection-history':
        return <VehicleTripInspectionHistorySection vehicle={vehicle} />;
      case 'preventative-maintenance':
        return <VehiclePreventativeMaintenanceSection vehicle={vehicle} />;
      case 'vehicle-defects':
        return <VehicleDefectsSection vehicle={vehicle} />;
      case 'vehicle-components':
        return <VehicleComponentsSection vehicle={vehicle} onUpdate={handleUpdateVehicle} />;
      case 'blue-book-rates':
        return <VehicleBlueBookRatesSection vehicle={vehicle} onUpdate={handleUpdateVehicle} />;
      case 'vehicle-attachments':
        return <VehicleAttachmentsSection vehicle={vehicle} />;
      case 'repair-history':
        return <VehicleRepairHistorySection vehicle={vehicle} />;
      default:
        return <VehicleAboutSection vehicle={vehicle} onUpdate={handleUpdateVehicle} canEdit={canEditVehicle} />;
    }
  };

  return (
    <div className="tw-w-full tw-p-4">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h1>Vehicle: {vehicle.unitNumber}</h1>
        <Button variant="secondary" onClick={() => navigate('/vehicles')}>
          Back to Vehicles
        </Button>
      </div>

      <Row>
        <Col md={3}>
          <VehicleSidebar
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
        </Col>
        <Col md={9}>
          {renderSection()}
        </Col>
      </Row>
    </div>
  );
};

export default VehicleDetailsPage;

